import { useMutation, useQuery, useSuspenseQuery, useQueryClient } from '@tanstack/react-query';
import { getAllStates, getAllStatesAdmin, getCountyByZip, manageState, StateAdmin } from '@uplift/api';
import { BaseQueryOpts, BaseSuspenseQueryOpts } from './baseTypes';


export const useStatesQuery = (opts?: BaseQueryOpts) => {
  return useQuery({
    queryKey: [ 'geo', 'states' ],
    queryFn: async () => {
      const response = await getAllStates();

      return response.data;
    },
    ...opts,
  });
};


export const useStateQuery = (stateId?: string, opts?: BaseQueryOpts) => {
  return useQuery({
    queryKey: [ 'geo', 'states' ],
    queryFn: async () => {
      const response = await getAllStates();

      return response.data;
    },
    select: data => data.find(s => s.id === stateId),
    enabled: !!stateId,
    ...opts,
  });
};


export const useCountyQuery = (zip?: string, opts?: BaseQueryOpts) => {
  return useQuery({
    queryKey: [ 'geo', 'county', zip ],
    queryFn: async () => {
      if (!zip) {
        return undefined;
      }

      const addressCountyResponse = await getCountyByZip(zip).then(res => res.data?.map(c => c.countyName).join(', ')).catch(() => undefined);

      return addressCountyResponse;
    },
    enabled: !!zip,
    ...opts,
  });
};


export const useAdminStatesQuery = (opts?: BaseQueryOpts) => {
  return useQuery({
    queryKey: [ 'geo', 'adminStates' ],
    queryFn: async () => {
      const response = await getAllStatesAdmin();

      return response.data;
    },
    ...opts,
  });
};


export const useAdminStateQuery = (stateId?: string, opts?: BaseSuspenseQueryOpts) => {
  return useSuspenseQuery({
    queryKey: [ 'geo', 'adminStates' ],
    queryFn: async () => {
      const response = await getAllStatesAdmin();

      return response.data;
    },
    select: data => stateId ? data.find(s => s.id === stateId) : null,
    ...opts,
  });
};


export const useManageStateMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ( values: StateAdmin ) => {
      return await manageState(values);
    },
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: [ 'geo', 'adminStates' ] }),
      ]);
    },
  });
};
